/**
 * StreamLain Block Effects
 *
 * Registers custom elements for all StreamLain blocks.
 * Each block component must be registered here for Lit to recognize it.
 */

import { registerStreamlainTrashcanHandler } from '@blocksuite/affine-shared/commands';

import { AutomationCanvasBackground } from './automation/components/automation-background.js';
import { AutomationCanvasMask } from './automation/components/automation-mask.js';
import { AutomationTitle } from './automation/components/automation-title.js';
import {
  AFFINE_AUTOMATION,
  AFFINE_AUTOMATION_CANVAS,
  AutomationBlockComponent,
  AutomationCanvasBlockComponent,
} from './automation/index.js';
import {
  AFFINE_BRUSH_HEADING_CANVAS,
  BrushHeadingCanvasBlockComponent,
} from './brush-heading/index.js';
import { ElsterCanvasBackground } from './elster/components/elster-background.js';
import {
  AFFINE_ELSTER,
  AFFINE_ELSTER_CANVAS,
  ElsterBlockComponent,
  ElsterCanvasBlockComponent,
} from './elster/index.js';
import { GhostCanvasBackground } from './ghost/ghost-background.js';
import {
  AFFINE_GHOST_CANVAS,
  GhostCanvasBlockComponent,
} from './ghost/index.js';
import { registerScreenshotHooks } from './hooks/screenshot-hooks.js';
import { AFFINE_MEMBER_PICKER, MemberPicker } from './member-picker/index.js';
import {
  AFFINE_STICKY_NOTE_CANVAS,
  StickyNoteCanvasBlockComponent,
} from './sticky-note/index.js';
import { AutomationConnectorToolButton } from './tools/toolbar/index.js';
import { isTrashable, moveToTrashcan } from './trashcan/trashcan-utils.js';
import {
  AFFINE_TUTORIAL_CARD_CANVAS,
  TutorialCardCanvasBlockComponent,
} from './tutorial-card/index.js';

export function effects() {
  // Register screenshot hooks for clipboard functionality
  // Enables parallel rendering and custom element categorization
  registerScreenshotHooks();

  // Register trashcan handler for delete command
  // This enables soft-delete for Database and Automation blocks
  registerStreamlainTrashcanHandler((store, model) => {
    if (model.flavour && isTrashable(model.flavour)) {
      return moveToTrashcan(
        store as Parameters<typeof moveToTrashcan>[0],
        model.id
      );
    }
    return false;
  });

  // Automation Block
  customElements.define(AFFINE_AUTOMATION, AutomationBlockComponent);
  customElements.define(
    AFFINE_AUTOMATION_CANVAS,
    AutomationCanvasBlockComponent
  );
  customElements.define(
    AFFINE_STICKY_NOTE_CANVAS,
    StickyNoteCanvasBlockComponent
  );
  customElements.define(
    AFFINE_BRUSH_HEADING_CANVAS,
    BrushHeadingCanvasBlockComponent
  );
  customElements.define(AFFINE_MEMBER_PICKER, MemberPicker);
  customElements.define(
    'automation-canvas-background',
    AutomationCanvasBackground
  );
  customElements.define('automation-canvas-mask', AutomationCanvasMask);
  customElements.define('affine-automation-title', AutomationTitle);

  // Ghost Block (E012-T013) - Canvas only
  customElements.define(AFFINE_GHOST_CANVAS, GhostCanvasBlockComponent);
  customElements.define('ghost-canvas-background', GhostCanvasBackground);

  // ELSTER Block (sl-yphr)
  customElements.define(AFFINE_ELSTER, ElsterBlockComponent);
  customElements.define(AFFINE_ELSTER_CANVAS, ElsterCanvasBlockComponent);
  customElements.define('elster-canvas-background', ElsterCanvasBackground);

  // Tutorial Card Block (sl-728o.7.1) - Canvas only
  customElements.define(
    AFFINE_TUTORIAL_CARD_CANVAS,
    TutorialCardCanvasBlockComponent
  );

  // Automation Connector Tool Button (sl-zfd.1)
  customElements.define(
    'automation-connector-tool-button',
    AutomationConnectorToolButton
  );
}
